import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import {
  Plus, Loader2, X, Save, Calendar as CalIcon, Target, Trophy, AlertTriangle,
  CheckCircle2, Clock, Trash2, Download, Pencil, BarChart3, TrendingUp, Building2,
  Sparkles, Phone, FileSpreadsheet,
} from "lucide-react";
import { toast } from "sonner";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import apiClient, { API_BASE } from "./api";
import { useAuth } from "./AuthContext";

const STATUS_COLOR = {
  pending: "bg-white/5 text-white/60 border-white/10",
  in_progress: "bg-[#8B73FF]/15 text-[#C9BCFF] border-[#8B73FF]/40",
  completed: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  overdue: "bg-red-500/15 text-red-300 border-red-500/40",
};
const STATUS_LABEL = {
  pending: "Pending",
  in_progress: "In progress",
  completed: "Completed",
  overdue: "Overdue",
};

const inputCls =
  "w-full bg-black/50 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-white/40 focus:border-[#FC5106] focus:ring-1 focus:ring-[#FC5106] outline-none transition-all";

export default function AdminTasks() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState("list"); // list | activations | reports
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [showNew, setShowNew] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [users, setUsers] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      const { data } = await apiClient.get("/api/admin/tasks", { params });
      setTasks(data);
    } catch {
      toast.error("Could not load tasks");
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    if (!isAdmin) return;
    try {
      const { data } = await apiClient.get("/api/admin/users");
      setUsers(data.filter((u) => u.active !== false));
    } catch { /* ignore */ }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);
  useEffect(() => { loadUsers(); /* eslint-disable-next-line */ }, []);

  const counts = useMemo(() => {
    const c = { total: tasks.length, pending: 0, in_progress: 0, completed: 0, overdue: 0 };
    tasks.forEach((t) => { c[t.status] = (c[t.status] || 0) + 1; });
    return c;
  }, [tasks]);

  const updateProgress = async (t, progress) => {
    try {
      const { data } = await apiClient.patch(`/api/admin/tasks/${t.id}`, { progress });
      setTasks((arr) => arr.map((x) => (x.id === t.id ? data : x)));
      if (data.status === "completed") toast.success(`${t.title} — completed!`);
    } catch { toast.error("Could not update progress"); }
  };

  const deleteTask = async (t) => {
    if (!confirm(`Delete "${t.title}"?`)) return;
    try {
      await apiClient.delete(`/api/admin/tasks/${t.id}`);
      setTasks((arr) => arr.filter((x) => x.id !== t.id));
      toast.success("Task deleted");
    } catch { toast.error("Delete failed"); }
  };

  const exportCsv = async () => {
    try {
      const token = localStorage.getItem("bf_token");
      const res = await fetch(`${API_BASE}/admin/tasks/export.csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `barknfurs-tasks-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch { toast.error("Export failed"); }
  };

  const generateDaily = async () => {
    try {
      const { data } = await apiClient.post("/api/admin/tasks/generate-daily?role=sales");
      toast.success(`${data.tasks_created} new daily task(s) created for ${data.users_processed} user(s)`);
      load();
    } catch { toast.error("Generation failed"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-tasks">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[#C9BCFF] uppercase tracking-[0.25em] text-xs mb-2">Tasks</p>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-white">
            {isAdmin ? "Assign & track daily targets" : "My tasks"}
          </h1>
          <p className="text-white/50 text-sm mt-1">
            {counts.total} total · {counts.completed} done · {counts.overdue} overdue
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <>
              <button onClick={generateDaily} data-testid="tasks-generate-daily" className="btn-secondary !py-2.5 !px-4 !text-sm">
                <Sparkles className="w-4 h-4" /> Generate today's tasks
              </button>
              <button onClick={exportCsv} data-testid="tasks-export" className="btn-secondary !py-2.5 !px-4 !text-sm">
                <Download className="w-4 h-4" /> Export CSV
              </button>
              <button onClick={() => setShowNew(true)} data-testid="tasks-new-btn" className="btn-primary !py-2.5 !px-4 !text-sm">
                <Plus className="w-4 h-4" /> Assign task
              </button>
            </>
          )}
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="Total" value={counts.total} icon={Target} color="#8B73FF" />
        <StatCard label="In progress" value={counts.in_progress} icon={Clock} color="#8B73FF" />
        <StatCard label="Completed" value={counts.completed} icon={CheckCircle2} color="#10b981" />
        <StatCard label="Overdue" value={counts.overdue} icon={AlertTriangle} color="#ef4444" />
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-white/10">
        <TabBtn active={tab === "list"} onClick={() => setTab("list")} testid="tab-list">
          <Target className="w-4 h-4" /> Tasks
        </TabBtn>
        <TabBtn active={tab === "activations"} onClick={() => setTab("activations")} testid="tab-activations">
          <Building2 className="w-4 h-4" /> Activations
        </TabBtn>
        {isAdmin && (
          <TabBtn active={tab === "reports"} onClick={() => setTab("reports")} testid="tab-reports">
            <BarChart3 className="w-4 h-4" /> Reports
          </TabBtn>
        )}
      </div>

      {tab === "list" && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            {["all", "pending", "in_progress", "completed", "overdue"].map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                data-testid={`tasks-filter-${s}`}
                className={`text-xs uppercase tracking-wider px-3 py-1.5 rounded-full border transition-colors ${
                  statusFilter === s
                    ? "bg-[#FC5106] border-[#FC5106] text-white"
                    : "bg-black/30 border-white/10 text-white/70 hover:border-white/20"
                }`}
              >
                {s === "all" ? "All" : STATUS_LABEL[s]}
              </button>
            ))}
          </div>

          <div className="rounded-2xl border border-white/10 bg-[#121212] overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-6 h-6 text-[#FC5106] animate-spin" />
              </div>
            ) : tasks.length === 0 ? (
              <p className="text-white/40 text-center py-16 text-sm">
                {isAdmin ? "No tasks yet — click \"Assign task\" to create one." : "No tasks assigned to you."}
              </p>
            ) : (
              <div className="divide-y divide-white/5">
                {tasks.map((t) => (
                  <TaskRow
                    key={t.id}
                    t={t}
                    isAdmin={isAdmin}
                    onProgress={(p) => updateProgress(t, p)}
                    onLeadsAdded={(updated) => setTasks((arr) => arr.map((x) => (x.id === updated.id ? updated : x)))}
                    onEdit={() => setEditTask(t)}
                    onDelete={() => deleteTask(t)}
                  />
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {tab === "reports" && isAdmin && <ReportsPanel />}
      {tab === "activations" && <ActivationsPanel isAdmin={isAdmin} />}

      {showNew && (
        <TaskModal
          users={users}
          onClose={() => setShowNew(false)}
          onSaved={(t) => setTasks((arr) => [t, ...arr])}
        />
      )}
      {editTask && (
        <TaskModal
          users={users}
          task={editTask}
          onClose={() => setEditTask(null)}
          onSaved={(t) => setTasks((arr) => arr.map((x) => (x.id === t.id ? t : x)))}
        />
      )}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-[#121212] p-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] uppercase tracking-wider text-white/50">{label}</p>
        <Icon className="w-4 h-4" style={{ color }} />
      </div>
      <p className="font-heading text-2xl font-bold text-white">{value}</p>
    </div>
  );
}

function TabBtn({ active, onClick, testid, children }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${
        active ? "border-[#FC5106] text-white" : "border-transparent text-white/50 hover:text-white"
      }`}
    >
      {children}
    </button>
  );
}

function TaskRow({ t, isAdmin, onProgress, onLeadsAdded, onEdit, onDelete }) {
  const target = t.target || 1;
  const progress = t.progress || 0;
  const pct = Math.min(100, Math.round((progress / target) * 100));
  const [edit, setEdit] = useState(false);
  const [val, setVal] = useState(progress);
  const isMine = !isAdmin; // sales/staff seeing their own
  const trackLeads = !!t.track_leads
    || t.template_key === "call_50_leads"
    || (/(call|follow.?up).*lead/i.test(t.title || ""));
  const leads = t.leads || [];
  const dueLabel = (() => {
    try { return format(new Date(t.due_date), "PPP"); } catch { return t.due_date; }
  })();

  return (
    <div className="p-4 hover:bg-white/[0.02]" data-testid={`task-row-${t.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-white font-semibold text-sm">{t.title}</p>
            <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${STATUS_COLOR[t.status]}`}>
              {STATUS_LABEL[t.status]}
            </span>
          </div>
          {t.description && <p className="text-white/60 text-xs mt-1 whitespace-pre-line">{t.description}</p>}
          <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-white/50">
            <span className="inline-flex items-center gap-1"><CalIcon className="w-3 h-3" />{dueLabel}</span>
            <span>·</span>
            <span>Assigned to <span className="text-white/80">{t.assignee_name || t.assignee_email}</span></span>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {isAdmin && (
            <>
              <button
                onClick={onEdit}
                data-testid={`task-edit-${t.id}`}
                className="p-2 rounded-full bg-white/5 hover:bg-white/10 text-white/70"
                aria-label="Edit"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={onDelete}
                data-testid={`task-delete-${t.id}`}
                className="p-2 rounded-full bg-white/5 hover:bg-red-500/20 hover:text-red-300 text-white/70"
                aria-label="Delete"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </>
          )}
        </div>
      </div>

      <div className="mt-3">
        <div className="flex items-center justify-between text-xs text-white/60 mb-1">
          <span>Progress</span>
          <span>
            <span className="text-white font-semibold">{progress}</span>
            <span className="text-white/40"> / {target} ({pct}%)</span>
          </span>
        </div>
        <div className="h-2 rounded-full bg-white/5 overflow-hidden">
          <div
            className="h-full rounded-full"
            style={{
              width: `${pct}%`,
              background: t.status === "completed"
                ? "linear-gradient(90deg, #10b981, #8B73FF)"
                : t.status === "overdue"
                  ? "linear-gradient(90deg, #ef4444, #f97316)"
                  : "linear-gradient(90deg, #FC5106, #8B73FF)",
            }}
          />
        </div>
        {isMine && t.status !== "completed" && !trackLeads && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {edit ? (
              <>
                <input
                  type="number"
                  min={0}
                  max={target * 10}
                  value={val}
                  onChange={(e) => setVal(Number(e.target.value))}
                  data-testid={`task-progress-input-${t.id}`}
                  className="w-24 bg-black/50 border border-white/10 rounded-xl px-3 py-1.5 text-sm text-white"
                />
                <button
                  onClick={() => { onProgress(val); setEdit(false); }}
                  data-testid={`task-progress-save-${t.id}`}
                  className="text-xs btn-primary !py-1.5 !px-3"
                >
                  Save
                </button>
                <button onClick={() => { setEdit(false); setVal(progress); }} className="text-xs text-white/50 hover:text-white">
                  Cancel
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => onProgress(Math.min(target, progress + 1))}
                  data-testid={`task-progress-plus-${t.id}`}
                  className="text-xs px-3 py-1.5 rounded-full bg-[#8B73FF]/10 border border-[#8B73FF]/30 text-[#C9BCFF] hover:bg-[#8B73FF]/20"
                >
                  +1
                </button>
                <button
                  onClick={() => onProgress(target)}
                  data-testid={`task-progress-done-${t.id}`}
                  className="text-xs px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/20"
                >
                  <CheckCircle2 className="inline w-3 h-3 mr-1" /> Mark complete
                </button>
                <button
                  onClick={() => setEdit(true)}
                  data-testid={`task-progress-edit-${t.id}`}
                  className="text-xs px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-white/70 hover:bg-white/10"
                >
                  Set value
                </button>
              </>
            )}
          </div>
        )}

        {trackLeads && (
          <TaskLeadsBlock
            t={t}
            leads={leads}
            isMine={isMine}
            onLeadsAdded={onLeadsAdded}
          />
        )}
      </div>
    </div>
  );
}

function TaskLeadsBlock({ t, leads, isMine, onLeadsAdded }) {
  const [showForm, setShowForm] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [rows, setRows] = useState([{ phone: "", name: "" }]);
  const [submitting, setSubmitting] = useState(false);

  const updateRow = (i, k, v) => setRows((arr) => arr.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const addRow = () => setRows((arr) => [...arr, { phone: "", name: "" }]);
  const removeRow = (i) => setRows((arr) => arr.length > 1 ? arr.filter((_, idx) => idx !== i) : arr);
  const validCount = rows.filter((r) => (r.phone || "").replace(/\D/g, "").length >= 10).length;

  const submit = async () => {
    const cleaned = rows
      .map((r) => ({ phone: (r.phone || "").trim(), name: (r.name || "").trim() }))
      .filter((r) => r.phone.replace(/\D/g, "").length >= 10);
    if (cleaned.length === 0) {
      toast.error("Add at least 1 valid 10-digit phone");
      return;
    }
    const norms = new Set();
    for (const r of cleaned) {
      const n = r.phone.replace(/\D/g, "").slice(-10);
      if (norms.has(n)) {
        toast.error(`Duplicate phone in your list: ${n}`);
        return;
      }
      norms.add(n);
    }
    setSubmitting(true);
    try {
      const { data } = await apiClient.post(`/api/admin/tasks/${t.id}/leads`, {
        leads: cleaned.map((r) => ({ phone: r.phone, name: r.name || undefined })),
      });
      toast.success(`${cleaned.length} lead(s) added — also created in Enquiries`);
      setRows([{ phone: "", name: "" }]);
      setShowForm(false);
      onLeadsAdded && onLeadsAdded(data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.duplicates) {
        const list = detail.duplicates.map((d) => `${d.phone} (already in ${d.source_type}: ${d.source_label} by ${d.rep})`).join("\n");
        toast.error(`Duplicate phones blocked:\n${list}`, { duration: 8000 });
      } else {
        toast.error(typeof detail === "string" ? detail : "Save failed");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-4 rounded-xl border border-white/10 bg-black/30 p-3 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Phone className="w-3.5 h-3.5 text-[#C9BCFF]" />
          <p className="text-xs uppercase tracking-wider text-white/60">
            Leads collected — <span className="text-white">{leads.length}</span> entered
          </p>
        </div>
        <div className="flex gap-2">
          {leads.length > 0 && (
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              data-testid={`task-leads-toggle-${t.id}`}
              className="text-xs px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-white/70 hover:bg-white/10"
            >
              {showAll ? "Hide list" : "View list"}
            </button>
          )}
          {isMine && t.status !== "completed" && !showForm && (
            <button
              type="button"
              onClick={() => setShowForm(true)}
              data-testid={`task-leads-add-${t.id}`}
              className="text-xs px-3 py-1.5 rounded-full bg-[#FC5106]/10 border border-[#FC5106]/30 text-[#FC5106] hover:bg-[#FC5106]/20"
            >
              <Plus className="inline w-3 h-3 mr-1" /> Add leads
            </button>
          )}
        </div>
      </div>

      {showAll && leads.length > 0 && (
        <div className="grid sm:grid-cols-2 gap-2" data-testid={`task-leads-list-${t.id}`}>
          {leads.map((l, i) => (
            <a
              key={i}
              href={`tel:${l.phone}`}
              className="flex items-center justify-between text-xs px-3 py-2 rounded-lg bg-black/40 border border-white/10 hover:border-[#8B73FF]/40"
            >
              <span>
                <span className="text-white/40 mr-2">{i + 1}.</span>
                <span className="text-white font-mono">{l.phone}</span>
                {l.name && <span className="text-white/60 ml-2">— {l.name}</span>}
              </span>
              <Phone className="w-3 h-3 text-[#C9BCFF]" />
            </a>
          ))}
        </div>
      )}

      {showForm && isMine && (
        <div className="space-y-2">
          {rows.map((r, i) => {
            const norm = (r.phone || "").replace(/\D/g, "");
            const valid = norm.length >= 10;
            return (
              <div key={i} className="grid grid-cols-12 gap-2 items-center">
                <p className="col-span-1 text-white/40 text-xs text-right">{i + 1}.</p>
                <input
                  data-testid={`task-${t.id}-lead-phone-${i}`}
                  type="tel"
                  inputMode="numeric"
                  placeholder="10-digit phone *"
                  value={r.phone}
                  onChange={(e) => updateRow(i, "phone", e.target.value)}
                  className={`col-span-5 bg-black/50 border ${
                    r.phone === "" ? "border-white/10" : valid ? "border-emerald-500/40" : "border-red-500/40"
                  } rounded-lg px-3 py-1.5 text-sm text-white placeholder-white/40 outline-none focus:ring-1 focus:ring-[#FC5106]`}
                />
                <input
                  data-testid={`task-${t.id}-lead-name-${i}`}
                  placeholder="Name (optional)"
                  value={r.name}
                  onChange={(e) => updateRow(i, "name", e.target.value)}
                  className="col-span-5 bg-black/50 border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white placeholder-white/40 outline-none focus:ring-1 focus:ring-[#FC5106]"
                />
                <button
                  type="button"
                  onClick={() => removeRow(i)}
                  disabled={rows.length === 1}
                  className="col-span-1 p-1.5 rounded-full bg-white/5 hover:bg-red-500/20 hover:text-red-300 text-white/60 disabled:opacity-30"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            );
          })}
          <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
            <button
              type="button"
              onClick={addRow}
              data-testid={`task-${t.id}-add-row`}
              className="text-xs px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-white/70 hover:bg-white/10"
            >
              <Plus className="inline w-3 h-3 mr-1" /> Add another
            </button>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => { setShowForm(false); setRows([{ phone: "", name: "" }]); }}
                className="text-xs text-white/50 hover:text-white px-3 py-1.5"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={submitting || validCount === 0}
                data-testid={`task-${t.id}-leads-submit`}
                className="text-xs btn-primary !py-1.5 !px-4 disabled:opacity-50"
              >
                {submitting ? <><Loader2 className="w-3 h-3 animate-spin" /> Saving</> : <><Save className="w-3 h-3" /> Submit {validCount} lead(s)</>}
              </button>
            </div>
          </div>
          <p className="text-[11px] text-white/40">
            Each lead is saved permanently and auto-creates an Enquiry — set follow-up reminders from /admin/enquiries.
          </p>
        </div>
      )}
    </div>
  );
}

function TaskModal({ users, task, onClose, onSaved }) {
  const editing = !!task;
  const [form, setForm] = useState({
    title: task?.title || "",
    description: task?.description || "",
    assignee_email: task?.assignee_email || (users[0]?.email || ""),
    target: task?.target || 10,
    track_leads: task?.track_leads ?? false,
  });
  const [date, setDate] = useState(task?.due_date ? new Date(task.due_date) : new Date());
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  // Auto-suggest track_leads when title mentions calling leads
  useEffect(() => {
    if (editing) return;
    if (/(call|follow.?up).*lead/i.test(form.title)) {
      setForm((p) => p.track_leads ? p : { ...p, track_leads: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.title]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.assignee_email || !date) {
      toast.error("Fill title, assignee and due date");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        title: form.title,
        description: form.description || undefined,
        assignee_email: form.assignee_email,
        target: Number(form.target),
        due_date: format(date, "yyyy-MM-dd"),
        track_leads: !!form.track_leads,
      };
      let data;
      if (editing) {
        ({ data } = await apiClient.patch(`/api/admin/tasks/${task.id}`, payload));
      } else {
        ({ data } = await apiClient.post("/api/admin/tasks", payload));
      }
      toast.success(editing ? "Task updated" : "Task assigned");
      onSaved(data);
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="task-modal"
    >
      <div
        className="w-full max-w-xl bg-[#0d0d0d] border border-white/10 rounded-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-[#0d0d0d]/95 backdrop-blur border-b border-white/10 px-6 py-4 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-white/50">{editing ? "Edit task" : "New task"}</p>
            <h2 className="font-heading text-lg font-bold text-white">
              {editing ? "Update assignment" : "Assign daily target"}
            </h2>
          </div>
          <button onClick={onClose} data-testid="task-modal-close" className="p-2 rounded-full bg-white/5 hover:bg-white/10 text-white">
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-white/50 mb-1.5 block">Title *</label>
            <input
              data-testid="task-title"
              value={form.title}
              onChange={(e) => update("title", e.target.value)}
              className={inputCls}
              placeholder="e.g. Call 20 leads"
              required
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-white/50 mb-1.5 block">Description</label>
            <textarea
              data-testid="task-description"
              rows={3}
              value={form.description}
              onChange={(e) => update("description", e.target.value)}
              className={`${inputCls} resize-none`}
              placeholder="Context, instructions, or sources"
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-white/50 mb-1.5 block">Assignee *</label>
              <Select value={form.assignee_email} onValueChange={(v) => update("assignee_email", v)}>
                <SelectTrigger data-testid="task-assignee" className="bg-black/50 border-white/10 text-white">
                  <SelectValue placeholder="Pick employee" />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10 text-white max-h-60">
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.email}>
                      {u.name} <span className="text-white/40 text-xs">· {u.role}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-white/50 mb-1.5 block">Target *</label>
              <input
                type="number"
                min={1}
                data-testid="task-target"
                value={form.target}
                onChange={(e) => update("target", e.target.value)}
                className={inputCls}
                placeholder="e.g. 20"
                required
              />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs uppercase tracking-wider text-white/50 mb-1.5 block">Due date *</label>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    data-testid="task-due-date"
                    className={`${inputCls} flex items-center justify-between text-left`}
                  >
                    {date ? format(date, "PPP") : "Pick a date"}
                    <CalIcon className="w-4 h-4 text-white/50" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-[#121212] border-white/10" align="start">
                  <Calendar mode="single" selected={date} onSelect={setDate} initialFocus className="text-white" />
                </PopoverContent>
              </Popover>
            </div>
            <div className="sm:col-span-2">
              <label className="flex items-start gap-3 cursor-pointer rounded-xl bg-black/40 border border-white/10 p-3 hover:border-[#FC5106]/40 transition-colors">
                <input
                  type="checkbox"
                  data-testid="task-track-leads"
                  checked={!!form.track_leads}
                  onChange={(e) => update("track_leads", e.target.checked)}
                  className="mt-1 accent-[#FC5106]"
                />
                <span>
                  <span className="text-white text-sm font-semibold flex items-center gap-2">
                    <Phone className="w-3.5 h-3.5 text-[#C9BCFF]" /> Track individual leads (require phone numbers)
                  </span>
                  <span className="text-white/50 text-xs block mt-0.5">
                    Sales must enter every lead's phone + name. Each becomes a follow-up Enquiry. Use for calling tasks (e.g. "Call 50 leads today").
                  </span>
                </span>
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2.5 text-sm font-semibold text-white/70 hover:text-white">
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              data-testid="task-submit"
              className="btn-primary !py-2.5 !px-5 !text-sm disabled:opacity-50"
            >
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><Save className="w-4 h-4" /> {editing ? "Update task" : "Assign task"}</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ReportsPanel() {
  const [days, setDays] = useState(14);
  const [daily, setDaily] = useState(null);
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [d1, d2] = await Promise.all([
          apiClient.get(`/api/admin/tasks/reports/daily?days=${days}`),
          apiClient.get(`/api/admin/tasks/reports/leaderboard?days=${days}`),
        ]);
        if (!cancelled) {
          setDaily(d1.data);
          setBoard(d2.data);
        }
      } catch { /* ignore */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [days]);

  if (loading || !daily || !board) {
    return <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 text-[#FC5106] animate-spin" /></div>;
  }

  const maxAssigned = Math.max(1, ...daily.series.map((r) => r.assigned));

  return (
    <div className="space-y-6" data-testid="tasks-reports">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs uppercase tracking-wider text-white/50">Window</span>
        {[7, 14, 30, 90].map((n) => (
          <button
            key={n}
            onClick={() => setDays(n)}
            data-testid={`tasks-window-${n}`}
            className={`text-xs px-3 py-1.5 rounded-full border ${
              days === n ? "bg-[#FC5106] border-[#FC5106] text-white" : "bg-black/30 border-white/10 text-white/70 hover:border-white/20"
            }`}
          >
            {n}d
          </button>
        ))}
      </div>

      {/* Totals */}
      <div className="grid grid-cols-3 gap-3">
        <StatCard label="Assigned" value={daily.totals.assigned} icon={Target} color="#8B73FF" />
        <StatCard label="Completed" value={daily.totals.completed} icon={CheckCircle2} color="#10b981" />
        <StatCard label="Completion rate" value={`${daily.totals.completion_rate}%`} icon={TrendingUp} color="#FC5106" />
      </div>

      {/* Daily completion chart */}
      <div className="rounded-2xl border border-white/10 bg-[#121212] p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-bold text-white">Daily completion</h3>
          <span className="text-xs text-white/50">Last {days} days</span>
        </div>
        <div className="space-y-2.5" data-testid="tasks-daily-chart">
          {daily.series.map((row) => {
            const pct = row.completion_rate;
            const widthAssigned = (row.assigned / maxAssigned) * 100;
            return (
              <div key={row.day} className="grid grid-cols-12 gap-3 items-center text-xs">
                <p className="col-span-3 sm:col-span-2 text-white/60 font-mono">{row.day.slice(5)}</p>
                <div className="col-span-7 sm:col-span-8 h-5 bg-white/5 rounded-md overflow-hidden relative">
                  <div className="absolute inset-y-0 left-0 bg-white/10" style={{ width: `${widthAssigned}%` }} />
                  <div
                    className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#FC5106] to-[#8B73FF]"
                    style={{ width: `${(row.completed / maxAssigned) * 100}%` }}
                  />
                </div>
                <p className="col-span-2 text-right">
                  <span className="text-white font-semibold">{row.completed}</span>
                  <span className="text-white/40">/{row.assigned}</span>
                  <span className="text-white/50 ml-1">({pct}%)</span>
                </p>
              </div>
            );
          })}
          {daily.series.every((r) => r.assigned === 0) && (
            <p className="text-white/40 text-sm text-center py-6">No tasks in this window yet.</p>
          )}
        </div>
      </div>

      {/* Leaderboard */}
      <div className="rounded-2xl border border-white/10 bg-[#121212] overflow-hidden">
        <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Trophy className="w-4 h-4 text-[#FC5106]" />
            <h3 className="font-heading font-bold text-white">Leaderboard</h3>
          </div>
          <span className="text-xs text-white/50">By completion rate</span>
        </div>
        {board.rows.length === 0 ? (
          <p className="text-white/40 text-sm text-center py-10">No data yet.</p>
        ) : (
          <table className="w-full text-sm" data-testid="tasks-leaderboard">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-white/50 bg-black/40">
                <th className="px-5 py-3 text-left font-medium">#</th>
                <th className="px-3 py-3 text-left font-medium">Employee</th>
                <th className="px-3 py-3 text-right font-medium">Assigned</th>
                <th className="px-3 py-3 text-right font-medium">Done</th>
                <th className="px-3 py-3 text-right font-medium">Overdue</th>
                <th className="px-5 py-3 text-right font-medium">Rate</th>
              </tr>
            </thead>
            <tbody>
              {board.rows.map((r, i) => (
                <tr key={r.email} className="border-t border-white/5">
                  <td className="px-5 py-3 text-white/40">{i + 1}</td>
                  <td className="px-3 py-3">
                    <p className="text-white font-medium">{r.name}</p>
                    <p className="text-white/40 text-xs">{r.email} · {r.role}</p>
                  </td>
                  <td className="px-3 py-3 text-right text-white/80">{r.assigned}</td>
                  <td className="px-3 py-3 text-right text-emerald-300">{r.completed}</td>
                  <td className="px-3 py-3 text-right text-red-300">{r.overdue}</td>
                  <td className="px-5 py-3 text-right">
                    <span className={`font-semibold ${
                      r.completion_rate >= 80 ? "text-emerald-300" :
                      r.completion_rate >= 50 ? "text-[#FC5106]" : "text-red-300"
                    }`}>
                      {r.completion_rate}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}


function ActivationsPanel({ isAdmin }) {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const reqs = [apiClient.get("/api/admin/activations")];
      if (isAdmin) reqs.push(apiClient.get("/api/admin/activations/summary?days=30"));
      const [list, sum] = await Promise.all(reqs);
      setItems(list.data);
      if (sum) setSummary(sum.data);
    } catch { toast.error("Could not load activations"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const removeOne = async (a) => {
    if (!confirm(`Delete activation at "${a.location_name}"?`)) return;
    try {
      await apiClient.delete(`/api/admin/activations/${a.id}`);
      setItems((arr) => arr.filter((x) => x.id !== a.id));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  const exportLedger = async () => {
    try {
      const token = localStorage.getItem("bf_token");
      const res = await fetch(`${API_BASE}/admin/activations/leads/export.csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `barknfurs-leads-ledger-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Ledger downloaded");
    } catch { toast.error("Export failed"); }
  };

  return (
    <div className="space-y-6" data-testid="activations-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-white/60 text-sm">
          Log offline outreach (apartment activations, society visits, vet collaborations) with the actual phone numbers of every lead — duplicates and fake counts are blocked.
          Today's activations auto-progress your "Call 50 leads" task.
        </p>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <button onClick={exportLedger} data-testid="leads-ledger-csv" className="btn-secondary !py-2.5 !px-4 !text-sm">
              <FileSpreadsheet className="w-4 h-4" /> Leads ledger CSV
            </button>
          )}
          <button onClick={() => setShowNew(true)} data-testid="activation-new-btn" className="btn-primary !py-2.5 !px-4 !text-sm">
            <Plus className="w-4 h-4" /> Log activation
          </button>
        </div>
      </div>

      {isAdmin && summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard label="Activations (30d)" value={summary.totals.activations} icon={Building2} color="#8B73FF" />
          <StatCard label="Leads collected" value={summary.totals.leads} icon={Target} color="#FC5106" />
          <StatCard label="Top performer" value={summary.by_user[0]?.name || "—"} icon={Trophy} color="#10b981" />
          <StatCard label="Active reps" value={summary.by_user.length} icon={CheckCircle2} color="#8B73FF" />
        </div>
      )}

      {isAdmin && summary && summary.by_user.length > 0 && (
        <div className="rounded-2xl border border-white/10 bg-[#121212] overflow-hidden">
          <div className="px-5 py-4 border-b border-white/10">
            <h3 className="font-heading font-bold text-white">Activations by rep — last 30 days</h3>
          </div>
          <table className="w-full text-sm" data-testid="activations-by-user">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-white/50 bg-black/40">
                <th className="px-5 py-3 text-left font-medium">Rep</th>
                <th className="px-3 py-3 text-right font-medium">Activations</th>
                <th className="px-5 py-3 text-right font-medium">Leads collected</th>
              </tr>
            </thead>
            <tbody>
              {summary.by_user.map((r) => (
                <tr key={r.email} className="border-t border-white/5">
                  <td className="px-5 py-3">
                    <p className="text-white">{r.name}</p>
                    <p className="text-white/40 text-xs">{r.email}</p>
                  </td>
                  <td className="px-3 py-3 text-right text-white/80">{r.activations}</td>
                  <td className="px-5 py-3 text-right text-[#FC5106] font-semibold">{r.leads_total}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="rounded-2xl border border-white/10 bg-[#121212] overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 text-[#FC5106] animate-spin" /></div>
        ) : items.length === 0 ? (
          <p className="text-white/40 text-center py-16 text-sm">
            No activations yet. {isAdmin ? "Sales reps will log apartment visits here." : "Tap \"Log activation\" after each visit."}
          </p>
        ) : (
          <div className="divide-y divide-white/5">
            {items.map((a) => (
              <ActivationRow key={a.id} a={a} isAdmin={isAdmin} onDelete={() => removeOne(a)} />
            ))}
          </div>
        )}
      </div>

      {showNew && (
        <ActivationModal
          onClose={() => setShowNew(false)}
          onSaved={(a) => { setItems((arr) => [a, ...arr]); load(); }}
        />
      )}
    </div>
  );
}

function ActivationRow({ a, isAdmin, onDelete }) {
  const [open, setOpen] = useState(false);
  const leads = a.leads || [];
  return (
    <div className="p-4 hover:bg-white/[0.02]" data-testid={`activation-row-${a.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="text-left flex-1 min-w-0"
          data-testid={`activation-toggle-${a.id}`}
        >
          <div className="flex items-center gap-2 flex-wrap">
            <Building2 className="w-4 h-4 text-[#C9BCFF]" />
            <p className="text-white font-semibold text-sm">{a.location_name}</p>
            <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#FC5106]/15 text-[#FC5106] border border-[#FC5106]/40">
              {a.leads_count} leads
            </span>
            <span className="text-[10px] text-white/40">{open ? "▲ hide phones" : "▼ show phones"}</span>
          </div>
          {a.notes && <p className="text-white/60 text-xs mt-1 whitespace-pre-line">{a.notes}</p>}
          <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-white/50">
            <span className="inline-flex items-center gap-1"><CalIcon className="w-3 h-3" />{a.date}</span>
            <span>·</span>
            <span>By <span className="text-white/80">{a.created_by_name || a.created_by}</span></span>
          </div>
        </button>
        {isAdmin && (
          <button
            onClick={onDelete}
            data-testid={`activation-delete-${a.id}`}
            className="p-2 rounded-full bg-white/5 hover:bg-red-500/20 hover:text-red-300 text-white/70"
            aria-label="Delete"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {open && leads.length > 0 && (
        <div className="mt-3 grid sm:grid-cols-2 gap-2 pl-6" data-testid={`activation-leads-${a.id}`}>
          {leads.map((l, i) => (
            <a
              key={i}
              href={`tel:${l.phone}`}
              className="flex items-center justify-between text-xs px-3 py-2 rounded-lg bg-black/40 border border-white/10 hover:border-[#8B73FF]/40"
            >
              <span>
                <span className="text-white/40 mr-2">{i + 1}.</span>
                <span className="text-white font-mono">{l.phone}</span>
                {l.name && <span className="text-white/60 ml-2">— {l.name}</span>}
              </span>
              <Phone className="w-3 h-3 text-[#C9BCFF]" />
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

function ActivationModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ location_name: "", notes: "" });
  const [leads, setLeads] = useState([{ phone: "", name: "" }]);
  const [date, setDate] = useState(new Date());
  const [submitting, setSubmitting] = useState(false);
  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const updateLead = (i, k, v) => setLeads((arr) => arr.map((l, idx) => idx === i ? { ...l, [k]: v } : l));
  const addLead = () => setLeads((arr) => [...arr, { phone: "", name: "" }]);
  const removeLead = (i) => setLeads((arr) => arr.length > 1 ? arr.filter((_, idx) => idx !== i) : arr);

  const validLeadCount = leads.filter((l) => (l.phone || "").replace(/\D/g, "").length >= 10).length;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.location_name || !date) {
      toast.error("Location and date are required");
      return;
    }
    const cleaned = leads
      .map((l) => ({ phone: (l.phone || "").trim(), name: (l.name || "").trim() }))
      .filter((l) => l.phone.replace(/\D/g, "").length >= 10);
    if (cleaned.length === 0) {
      toast.error("Add at least 1 valid 10-digit lead phone");
      return;
    }
    // Local dupe check
    const norms = new Set();
    for (const l of cleaned) {
      const n = l.phone.replace(/\D/g, "").slice(-10);
      if (norms.has(n)) {
        toast.error(`Duplicate phone in your list: ${n}`);
        return;
      }
      norms.add(n);
    }
    setSubmitting(true);
    try {
      const payload = {
        location_name: form.location_name,
        date: format(date, "yyyy-MM-dd"),
        leads: cleaned.map((l) => ({ phone: l.phone, name: l.name || undefined })),
        notes: form.notes || undefined,
      };
      const { data } = await apiClient.post("/api/admin/activations", payload);
      toast.success(`Activation logged with ${data.leads_count} verified leads`);
      onSaved(data);
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.duplicates) {
        const list = detail.duplicates.map((d) => `${d.phone} (already at ${d.previous_location} by ${d.previous_rep})`).join("\n");
        toast.error(`Some phones already exist:\n${list}`, { duration: 8000 });
      } else {
        toast.error(typeof detail === "string" ? detail : "Save failed");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="activation-modal"
    >
      <div className="w-full max-w-2xl bg-[#0d0d0d] border border-white/10 rounded-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-[#0d0d0d]/95 backdrop-blur border-b border-white/10 px-6 py-4 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-white/50">Outreach</p>
            <h2 className="font-heading text-lg font-bold text-white">Log apartment activation</h2>
          </div>
          <button onClick={onClose} className="p-2 rounded-full bg-white/5 hover:bg-white/10 text-white"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-white/50 mb-1.5 block">Location / society *</label>
            <input
              data-testid="activation-location"
              value={form.location_name}
              onChange={(e) => update("location_name", e.target.value)}
              className={inputCls}
              placeholder="e.g. Sunshine Apartments, Sailashree Vihar"
              required
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-white/50 mb-1.5 block">Date *</label>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  data-testid="activation-date"
                  className={`${inputCls} flex items-center justify-between text-left`}
                >
                  {date ? format(date, "PPP") : "Pick a date"}
                  <CalIcon className="w-4 h-4 text-white/50" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-[#121212] border-white/10" align="start">
                <Calendar mode="single" selected={date} onSelect={setDate} initialFocus className="text-white" />
              </PopoverContent>
            </Popover>
          </div>

          <div className="rounded-xl border border-white/10 bg-black/30 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wider text-white/50">Leads collected — phone numbers required</p>
                <p className="text-white text-sm font-semibold mt-0.5">
                  {validLeadCount} verified · <span className="text-white/40">{leads.length} total rows</span>
                </p>
              </div>
              <button
                type="button"
                onClick={addLead}
                data-testid="lead-add-row"
                className="text-xs px-3 py-1.5 rounded-full bg-[#FC5106]/10 border border-[#FC5106]/30 text-[#FC5106] hover:bg-[#FC5106]/20"
              >
                <Plus className="w-3 h-3 inline mr-1" /> Add lead
              </button>
            </div>
            <div className="space-y-2 max-h-72 overflow-y-auto pr-1" data-testid="leads-list">
              {leads.map((l, i) => {
                const norm = (l.phone || "").replace(/\D/g, "");
                const valid = norm.length >= 10;
                return (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center">
                    <p className="col-span-1 text-white/40 text-xs text-right">{i + 1}.</p>
                    <input
                      data-testid={`lead-phone-${i}`}
                      type="tel"
                      inputMode="numeric"
                      placeholder="10-digit phone *"
                      value={l.phone}
                      onChange={(e) => updateLead(i, "phone", e.target.value)}
                      className={`col-span-5 bg-black/50 border ${
                        l.phone === "" ? "border-white/10" : valid ? "border-emerald-500/40" : "border-red-500/40"
                      } rounded-xl px-3 py-2 text-sm text-white placeholder-white/40 outline-none focus:ring-1 focus:ring-[#FC5106]`}
                    />
                    <input
                      data-testid={`lead-name-${i}`}
                      placeholder="Name (optional)"
                      value={l.name}
                      onChange={(e) => updateLead(i, "name", e.target.value)}
                      className="col-span-5 bg-black/50 border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder-white/40 outline-none focus:ring-1 focus:ring-[#FC5106]"
                    />
                    <button
                      type="button"
                      onClick={() => removeLead(i)}
                      data-testid={`lead-remove-${i}`}
                      disabled={leads.length === 1}
                      className="col-span-1 p-1.5 rounded-full bg-white/5 hover:bg-red-500/20 hover:text-red-300 text-white/60 disabled:opacity-30 disabled:cursor-not-allowed"
                      aria-label="Remove"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
            <p className="text-[11px] text-white/40">
              Each phone is saved permanently and shown to admin in the Leads ledger. Duplicate phones across activations are blocked.
            </p>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-white/50 mb-1.5 block">Notes</label>
            <textarea
              rows={2}
              data-testid="activation-notes"
              value={form.notes}
              onChange={(e) => update("notes", e.target.value)}
              className={`${inputCls} resize-none`}
              placeholder="Audience response, plans for follow-up, etc."
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2.5 text-sm font-semibold text-white/70 hover:text-white">Cancel</button>
            <button
              type="submit"
              disabled={submitting || validLeadCount === 0}
              data-testid="activation-submit"
              className="btn-primary !py-2.5 !px-5 !text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><Save className="w-4 h-4" /> Save activation ({validLeadCount})</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
